import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Subscription, filter, take } from 'rxjs';
import { AuthRefreshService } from 'src/app/auth/services/auth-refresh.service';
import { isAllowedByScopes, scopesFor } from 'src/app/auth/services/role-scope';
import { captureReturnUrl, routePathOf } from 'src/app/auth/utils/return-url';
import { Auth } from 'src/app/auth/models/auth';
import { State, selectAuth } from 'src/app/store/auth/auth.reducers';
import { OverlayTeardownService } from 'src/app/core/services/overlay-teardown.service';
import { environment } from 'src/environments/environment';
import {
  OFFLINE_IDENTITY_STORAGE_KEY,
  forgetOfflineIdentity,
  isAuthRejection,
  readOfflineIdentity,
} from './offline-identity';
import { OfflineModeState } from './offline-mode.state';
import { offlineHomeFor } from './offline-routes';

/** How often an offline tab asks whether the server is back, while the browser claims a network. */
export const OFFLINE_RECONNECT_INTERVAL_MS = 30_000;

/** The browser surface this service touches, behind a token so the specs drive it. */
export interface OfflineSessionPlatform {
  isOnline(): boolean;
  now(): number;
  /** Adds a window listener; returns its remover. */
  listen(type: 'online' | 'storage', handler: (event: Event) => void): () => void;
  every(ms: number, fn: () => void): () => void;
}

export const OFFLINE_SESSION_PLATFORM = new InjectionToken<OfflineSessionPlatform>(
  'OFFLINE_SESSION_PLATFORM',
  {
    providedIn: 'root',
    factory: (): OfflineSessionPlatform => ({
      isOnline: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
      now: () => Date.now(),
      listen: (type, handler) => {
        window.addEventListener(type, handler);
        return () => window.removeEventListener(type, handler);
      },
      every: (ms, fn) => {
        const id = setInterval(fn, ms);
        return () => clearInterval(id);
      },
    }),
  },
);

/**
 * Runs offline mode once `AuthRefreshService.bootstrap` has entered it (a boot whose refresh failed
 * for want of NETWORK, with a valid remembered identity -- see `offline-identity.ts`):
 *
 *  - **Reconnects.** On the browser's `online` event and every {@link OFFLINE_RECONNECT_INTERVAL_MS}
 *    while the browser reports a network, it runs ONE ordinary `AuthRefreshService.refresh()` (the
 *    interceptor lets `/v2/refresh` through and refuses every other API call while offline). Success
 *    applies the session the normal way -- `applyToken` dispatches `APPLY_SESSION`, broadcasts,
 *    remembers the identity -- and offline mode simply ends: the tab stays on the catalogue page it
 *    was on, now online -- unless the boot had asked for a page offline mode could not serve
 *    (`OfflineModeState.returnUrl`) and the tab is still where the guards sent it instead (the URL
 *    it LANDED on, after the router's redirects: `OfflineModeState.landedUrl`): then it goes back to
 *    that page (product owner 25/10/2026), or to the user's home when the session's scopes do not
 *    allow it. A refusal from the SERVER (401 or 403, `isAuthRejection`) means
 *    the cookie is dead: the identity is forgotten and the tab goes to /auth, the requested page kept
 *    as the return URL of the login. Any other failure (a network failure, a 5xx during a deploy, a
 *    captive portal's HTML answer that does not parse) changes nothing: the next attempt follows.
 *  - **Ends when the session comes back by any other road** -- a sibling tab's refresh broadcasts a
 *    login, `AuthSyncService` applies it here, `isAuthenticated` turns true -- and that is the only
 *    thing it reads off the store.
 *  - **Agrees with the other tabs.** The identity lives in `localStorage`, shared by the origin, so a
 *    logout, a 401 bounce or a reconnect failure in ANY tab removes it, and the `storage` event ends
 *    offline mode here too (to /auth). Two offline tabs therefore enter, stay and leave together.
 *  - **Another user's login** (a sibling tab, or a reconnect on a cookie someone else refreshed)
 *    ends it the same way WHICHEVER SIGNAL COMES FIRST -- the broadcast session (`resume`) or the
 *    identity written to `localStorage` (`onStorage`): the overlays closed, /auth, where the incoming
 *    user's own post-auth rule takes over (review round 6: one order left the outgoing user's Scan on
 *    screen under the new session, the other went to /auth).
 *  - **Expires.** Past the identity's `validUntil` the cookie it stood for is dead, so the tab goes to
 *    /auth even with no network to confirm it.
 *
 * Constructed from `AppComponent` (`init()`), like the other root stores.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSessionService implements OnDestroy {
  private readonly subs = new Subscription();
  private stopTimers: (() => void)[] = [];
  private reconnecting = false;
  private initialised = false;

  constructor(
    private readonly mode: OfflineModeState,
    private readonly router: Router,
    private readonly store: Store<State>,
    private readonly authRefresh: AuthRefreshService,
    private readonly zone: NgZone,
    @Inject(OFFLINE_SESSION_PLATFORM) private readonly platform: OfflineSessionPlatform,
    private readonly overlays: OverlayTeardownService,
  ) {}

  init(): void {
    if (this.initialised) {
      return;
    }
    this.initialised = true;
    this.subs.add(
      this.mode.active$.subscribe((active) => (active ? this.start() : this.stop())),
    );
    // Where the tab lands in offline mode, after the router's redirects (see resume). The boot's
    // navigation normally ends after this; one that ended before is read off the router.
    if (this.mode.active && this.router.navigated) {
      this.mode.markLanded(this.router.url);
    }
    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e) => this.mode.markLanded(e.urlAfterRedirects)),
    );
    this.subs.add(
      this.store.pipe(select(selectAuth)).subscribe((auth) => {
        if (auth?.isAuthenticated && this.mode.active) {
          this.resume(auth);
        }
      }),
    );
    // Always on, not only while offline: a tab that is online can learn nothing from it, and the
    // listener costs nothing. Kept unconditional so a storage event racing the entry is not lost.
    this.stopTimers.push(this.platform.listen('storage', (e) => this.onStorage(e as StorageEvent)));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stop();
    this.stopTimers.forEach((stop) => stop());
    this.stopTimers = [];
  }

  /** One reconnect attempt. Public for the specs and for a "retry" affordance. */
  tryReconnect(): void {
    if (!this.mode.active || this.reconnecting) {
      return;
    }
    const identity = this.mode.identity;
    if (identity && identity.validUntil <= this.platform.now()) {
      this.leave();
      return;
    }
    if (!this.platform.isOnline()) {
      return;
    }
    this.reconnecting = true;
    this.authRefresh.refresh().subscribe({
      next: () => {
        this.reconnecting = false;
        // `applyToken` dispatched APPLY_SESSION before the refresh resolved, so the store already
        // holds the session -- and its subscription above may have resumed already (then this no-ops).
        this.store.pipe(select(selectAuth), take(1)).subscribe((auth) => this.resume(auth));
      },
      error: (e) => {
        this.reconnecting = false;
        // Only the server refusing the cookie ends offline mode. A 5xx (a deploy), a parse error
        // (a captive portal's HTML page), a network failure: the tab stays offline and tries again.
        // An identity switch applied its session: the store subscription resumes.
        if (isAuthRejection(e)) {
          this.leave();
        }
      },
    });
  }

  private reconnectStops: (() => void)[] = [];

  private start(): void {
    if (this.reconnectStops.length > 0) {
      return;
    }
    // Outside the zone: an idle interval would otherwise schedule change detection every tick for
    // nothing. The attempt itself re-enters, since it may navigate and repaint.
    this.zone.runOutsideAngular(() => {
      this.reconnectStops.push(
        this.platform.listen('online', () => this.zone.run(() => this.tryReconnect())),
      );
      this.reconnectStops.push(
        this.platform.every(OFFLINE_RECONNECT_INTERVAL_MS, () =>
          this.zone.run(() => this.tryReconnect()),
        ),
      );
    });
  }

  private stop(): void {
    this.reconnectStops.forEach((stop) => stop());
    this.reconnectStops = [];
  }

  private onStorage(event: StorageEvent): void {
    // `key === null` is a `localStorage.clear()` in another tab.
    if (!this.mode.active || (event.key !== null && event.key !== OFFLINE_IDENTITY_STORAGE_KEY)) {
      return;
    }
    const current = this.mode.identity;
    const stored = readOfflineIdentity(this.platform.now());
    if (!stored || !current) {
      this.zone.run(() => this.leave());
    } else if (stored.userId !== current.userId) {
      // another user logged in from a sibling tab: this offline session is over, but the identity
      // now stored is THEIRS (valid, just written): forgetting it would take their offline access
      // away too (review round 4, 26/10/2026). Nor is this user's requested page theirs to land on.
      this.zone.run(() => this.endForAnotherUser());
    }
  }

  /**
   * Another user's session (or identity) replaced the offline user's: ONE ending, whichever signal
   * came first. This tab's offline mode had no session, so the cross-tab rescue of an identity switch
   * (`AuthSyncService`, `classifyIdentity`: an empty store reads "same") does not run here: the
   * overlays are closed and the outgoing user's screen left for /auth, where the incoming session, as
   * soon as it is applied (before or after), is routed by its own post-auth rule. The identity stored
   * is the incoming user's: kept.
   */
  private endForAnotherUser(): void {
    this.overlays.closeAll('identity-switch');
    this.mode.exit();
    this.router.navigateByUrl('/auth').then();
  }

  /**
   * A session is back and offline mode ends. The tab stays where it is, except on the offline home
   * the guards sent it to instead of the page the boot asked for: then it goes back there. The scope
   * check is the one RoleScopeGuard would make (refused: the user's home, as online); route `roles`
   * are left to CheckAuthService, which sends a refusal to the home page itself. The page is the
   * offline user's: a session of ANOTHER user (a login in a sibling tab) ends offline mode as the
   * `storage` event would (`endForAnotherUser`; review rounds 4 and 6).
   *
   * "Where the guards sent it" is the URL the tab LANDED on (`OfflineModeState.landedUrl`), not the
   * offline home: `/app/ingest/scan` redirects to `/app/ingest/scan/scan`, and the comparison with
   * the home never matched for anyone landing on Scan (review round 6). Before any navigation ended
   * (a spec, an early reconnect) the offline home stands in for it.
   */
  private resume(auth: Pick<Auth, 'userId' | 'roles'> | null | undefined): void {
    if (!this.mode.active) {
      return;
    }
    const target = this.mode.returnUrl;
    const landed = this.mode.landedUrl ?? offlineHomeFor(this.mode.identity?.roles);
    const sameUser = !!auth && auth.userId === this.mode.identity?.userId;
    if (!sameUser && auth?.userId) {
      this.endForAnotherUser();
      return;
    }
    this.mode.exit();
    if (!target || !sameUser || pathOf(this.router.url ?? '') !== pathOf(landed)) {
      return;
    }
    const scopes = scopesFor(auth?.roles);
    const destination = isAllowedByScopes(scopes, target) ? target : scopes[0]?.home ?? environment.homePage;
    this.router.navigateByUrl(destination).then();
  }

  /** Offline mode is over and no session replaced it: forget the identity, show the login. */
  private leave(): void {
    // The page the boot asked for survives the login, as it would have without offline mode, and
    // only for the user it was asked as (the offline identity, still set here: `exit` comes after);
    // another user signing into this tab goes home, as after an online session expiry (review
    // round 7).
    captureReturnUrl(this.mode.returnUrl, this.mode.identity?.userId);
    forgetOfflineIdentity();
    this.mode.exit();
    this.router.navigateByUrl('/auth').then();
  }
}

/** A URL's route path without the trailing slash: what tells two places of the app apart. */
function pathOf(url: string): string {
  return routePathOf(url).replace(/\/+$/, '');
}
